import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Home, ChevronDown, ChevronUp, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition"
        onClick={() => setOpen(!open)}
      >
        <span className="font-semibold text-slate-800">{question}</span>
        {open ? (
          <ChevronUp className="w-5 h-5 text-yellow-500 flex-shrink-0 ml-4" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0 ml-4" />
        )}
      </button>
      {open && (
        <div className="px-6 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function PublicCMSPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/public/pages/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPage(data);
      } else if (response.status === 404) {
        setError('Page non trouvée');
      } else {
        setError('Erreur lors du chargement de la page');
      }
    } catch (err) {
      console.error('Error fetching page:', err);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-4xl font-bold text-slate-800 mb-4">404</h1>
        <p className="text-slate-600 mb-6">{error}</p>
        <Link to="/portail">
          <Button className="bg-yellow-500 hover:bg-yellow-600">
            <Home className="w-4 h-4 mr-2" />
            Retour à l'accueil
          </Button>
        </Link>
      </div>
    );
  }

  if (!page) return null;

  const renderCustomSection = (section) => {
    const variant = section.settings?.variant;
    const product = section.settings?.product;

    // Tableau de specs comparatif CPD-40 vs CPD-60
    if (variant === 'specs-table' && product === 'cpd') {
      return (
        <section key={section.id} className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-800 mb-2 text-center">{section.title}</h2>
            {section.subtitle && (
              <p className="text-center text-slate-500 mb-2">{section.subtitle}</p>
            )}
            {section.content && (
              <p className="text-center text-slate-600 mb-8">{section.content}</p>
            )}
            <div className="overflow-x-auto rounded-2xl shadow-md border border-slate-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    <th className="px-5 py-4 text-left font-semibold">Caractéristique</th>
                    <th className="px-5 py-4 text-center font-semibold">
                      <span className="inline-block bg-yellow-500 text-slate-900 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">CPD-40</span>
                    </th>
                    <th className="px-5 py-4 text-center font-semibold">
                      <span className="inline-block bg-yellow-400 text-slate-900 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">CPD-60</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {section.items && section.items.map((row, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-5 py-3 font-medium text-slate-700">{row.label}</td>
                      <td className="px-5 py-3 text-center text-slate-600">{row.cpd40}</td>
                      <td className="px-5 py-3 text-center text-slate-600 font-medium">{row.cpd60}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      );
    }

    // Tableau des modèles TMBP
    if (variant === 'specs-table' && product === 'tmbp') {
      return (
        <section key={section.id} className="py-16 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-800 mb-2 text-center">{section.title}</h2>
            {section.subtitle && (
              <p className="text-center text-slate-500 mb-2">{section.subtitle}</p>
            )}
            {section.content && (
              <p className="text-center text-slate-600 mb-8">{section.content}</p>
            )}
            <div className="overflow-x-auto rounded-2xl shadow-md border border-slate-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-900 text-white">
                    <th className="px-4 py-4 text-left font-semibold">Modèle</th>
                    <th className="px-4 py-4 text-center font-semibold">Hauteur flèche</th>
                    <th className="px-4 py-4 text-center font-semibold">Débit (m³/h)</th>
                    <th className="px-4 py-4 text-center font-semibold">Type flèche</th>
                    <th className="px-4 py-4 text-center font-semibold">Châssis</th>
                    <th className="px-4 py-4 text-center font-semibold">Puissance</th>
                    <th className="px-4 py-4 text-center font-semibold">Poids</th>
                  </tr>
                </thead>
                <tbody>
                  {section.items && section.items.map((row, idx) => (
                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="px-4 py-3 font-bold text-yellow-600">{row.modele}</td>
                      <td className="px-4 py-3 text-center text-slate-700 font-medium">{row.hauteur}</td>
                      <td className="px-4 py-3 text-center text-slate-600">{row.debit}</td>
                      <td className="px-4 py-3 text-center text-slate-600">{row.fleche}</td>
                      <td className="px-4 py-3 text-center text-slate-600">{row.chassis}</td>
                      <td className="px-4 py-3 text-center text-slate-600">{row.puissance}</td>
                      <td className="px-4 py-3 text-center text-slate-600">{row.poids}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      );
    }

    // Bloc réassurance
    if (variant === 'reassurance') {
      const points = section.content ? section.content.split('. ').filter(Boolean) : [];
      return (
        <section key={section.id} className="py-16 bg-yellow-50 border-y border-yellow-100">
          <div className="max-w-4xl mx-auto px-4 text-center">
            {section.title && (
              <h2 className="text-3xl font-bold text-slate-800 mb-8">{section.title}</h2>
            )}
            {points.length > 2 ? (
              <ul className="text-left space-y-4 max-w-2xl mx-auto">
                {points.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-slate-700">
                    <CheckCircle2 className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <span>{point}{!point.endsWith('.') ? '.' : ''}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-lg text-slate-700 leading-relaxed">{section.content}</p>
            )}
          </div>
        </section>
      );
    }

    // Custom générique (HTML)
    return (
      <section key={section.id} className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          {section.title && (
            <h2 className="text-3xl font-bold text-slate-800 mb-4 text-center">{section.title}</h2>
          )}
          {section.subtitle && (
            <p className="text-lg text-slate-600 mb-8 text-center">{section.subtitle}</p>
          )}
          {section.content && (
            <div
              className="prose max-w-none text-slate-700"
              dangerouslySetInnerHTML={{ __html: section.content }}
            />
          )}
        </div>
      </section>
    );
  };

  const renderSection = (section) => {
    if (!section.visible) return null;

    switch (section.type) {
      case 'hero':
        return (
          <section
            key={section.id}
            className="relative py-24 bg-gradient-to-br from-slate-900 to-slate-800 text-white"
            style={section.image_url ? {
              backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.7)), url(${section.image_url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            } : {}}
          >
            <div className="max-w-5xl mx-auto px-4 text-center">
              {section.title && (
                <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                  {section.title}
                </h1>
              )}
              {section.subtitle && (
                <p className="text-xl text-yellow-300 mb-6 font-medium">{section.subtitle}</p>
              )}
              {section.content && (
                <p className="max-w-3xl mx-auto text-lg text-slate-300 leading-relaxed">
                  {section.content}
                </p>
              )}
            </div>
          </section>
        );

      case 'text':
        return (
          <section key={section.id} className="py-16 bg-white">
            <div className="max-w-4xl mx-auto px-4">
              {section.title && (
                <h2 className="text-3xl font-bold text-slate-800 mb-4 text-center">{section.title}</h2>
              )}
              {section.subtitle && (
                <p className="text-lg text-slate-600 mb-8 text-center">{section.subtitle}</p>
              )}
              {section.content && (
                <div className="prose prose-lg max-w-none text-slate-700 whitespace-pre-line">
                  {section.content}
                </div>
              )}
            </div>
          </section>
        );

      case 'features':
        return (
          <section key={section.id} className="py-16 bg-slate-50">
            <div className="max-w-5xl mx-auto px-4">
              {section.title && (
                <h2 className="text-3xl font-bold text-slate-800 mb-4 text-center">{section.title}</h2>
              )}
              {section.subtitle && (
                <p className="text-lg text-slate-600 mb-12 text-center">{section.subtitle}</p>
              )}
              {section.content && (
                <p className="max-w-4xl mx-auto text-slate-700 mb-8 text-center">{section.content}</p>
              )}
              {section.items && section.items.length > 0 && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                  {section.items.map((item, idx) => (
                    <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-slate-100 hover:shadow-md transition">
                      {item.title && (
                        <h3 className="font-bold text-base text-slate-800 mb-2 flex items-center gap-2">
                          <span className="w-2 h-2 bg-yellow-500 rounded-full flex-shrink-0"></span>
                          {item.title}
                        </h3>
                      )}
                      {(item.description || item.content) && (
                        <p className="text-sm text-slate-600">{item.description || item.content}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        );

      case 'faq':
        return (
          <section key={section.id} className="py-16 bg-white">
            <div className="max-w-3xl mx-auto px-4">
              {section.title && (
                <h2 className="text-3xl font-bold text-slate-800 mb-4 text-center">{section.title}</h2>
              )}
              {section.subtitle && (
                <p className="text-lg text-slate-500 mb-10 text-center">{section.subtitle}</p>
              )}
              {section.items && section.items.length > 0 && (
                <div className="space-y-3">
                  {section.items.map((item, idx) => (
                    <FaqItem
                      key={idx}
                      question={item.question || item.title}
                      answer={item.answer || item.content}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
        );

      case 'cta':
        return (
          <section
            key={section.id}
            className="py-16 bg-yellow-500"
            style={section.image_url ? {
              backgroundImage: `linear-gradient(rgba(245,158,11,0.92), rgba(245,158,11,0.92)), url(${section.image_url})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            } : {}}
          >
            <div className="max-w-3xl mx-auto px-4 text-center">
              {section.title && (
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
                  {section.title}
                </h2>
              )}
              {section.subtitle && (
                <p className="text-yellow-100 text-lg mb-2">{section.subtitle}</p>
              )}
              {section.content && (
                <p className="text-yellow-100 mb-8">{section.content}</p>
              )}
              <Link to={section.settings?.button_link || '/portail/contact'}>
                <Button size="lg" className="bg-slate-900 hover:bg-slate-800 text-white text-base px-8 py-4 h-auto">
                  {section.settings?.button_text || 'Nous contacter'}
                </Button>
              </Link>
            </div>
          </section>
        );

      case 'contact':
        return (
          <section key={section.id} className="py-16 bg-white">
            <div className="max-w-3xl mx-auto px-4 text-center">
              {section.title && (
                <h2 className="text-3xl font-bold text-slate-800 mb-4">{section.title}</h2>
              )}
              {section.content && (
                <p className="text-lg text-slate-600 mb-8">{section.content}</p>
              )}
              <Link to="/portail/contact">
                <Button size="lg" className="bg-yellow-500 hover:bg-yellow-600">
                  Contactez-nous
                </Button>
              </Link>
            </div>
          </section>
        );

      case 'custom':
        return renderCustomSection(section);

      default:
        return (
          <section key={section.id} className="py-16 bg-white">
            <div className="max-w-4xl mx-auto px-4">
              {section.title && (
                <h2 className="text-3xl font-bold text-slate-800 mb-4">{section.title}</h2>
              )}
              {section.content && (
                <div className="text-slate-700 whitespace-pre-line">{section.content}</div>
              )}
            </div>
          </section>
        );
    }
  };

  return (
    <div className="min-h-screen">
      {page.sections.length === 0 || page.sections[0]?.type !== 'hero' ? (
        <div className="bg-slate-100 py-12">
          <div className="max-w-5xl mx-auto px-4">
            <h1 className="text-4xl font-bold text-slate-800">{page.title}</h1>
            {page.meta_description && (
              <p className="text-lg text-slate-600 mt-2">{page.meta_description}</p>
            )}
          </div>
        </div>
      ) : null}

      {page.sections
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(section => renderSection(section))}

      {page.sections.length === 0 && (
        <div className="py-20 text-center text-slate-500">
          <p>Cette page n'a pas encore de contenu.</p>
        </div>
      )}
    </div>
  );
}
